import {
  ICreateSection,
  IImportActivities,
  IImportSections,
  IMoveActivityBetweenSections,
  IRemoveSection,
  IUpdateSection
} from '@sdk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';

import { sdk } from '@/services';

const SectionService = sdk.getService('SectionService');
const CourseService = sdk.getService('CourseService');

export const useMutateSection = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const onSuccess = () => queryClient.invalidateQueries({ queryKey: ['courses'] });
  const { t } = useTranslation('CourseTranslations');

  const { mutate: createSection, isPending: isCreating } = useMutation({
    mutationFn: (create: ICreateSection) => SectionService.createSection(create),
    onSuccess
  });

  const { mutate: updateSection, isPending: isUpdating } = useMutation({
    mutationFn: (section: IUpdateSection) => SectionService.updateSection(section),
    onSuccess: () => {
      onSuccess();
      message.success(t('SECTION_UPDATED'));
    }
  });

  const { mutate: removeSection, isPending: isRemoving } = useMutation({
    mutationFn: (remove: IRemoveSection) => SectionService.removeSection(remove),
    onSuccess: () => {
      onSuccess();
      message.success(t('SECTION_DELETED_SUCCESSFULLY'));
    }
  });

  const { mutate: moveActivityBetweenSections, isPending: isMoving } = useMutation({
    mutationFn: (move: IMoveActivityBetweenSections) =>
      SectionService.moveActivityBetweenSections(move),
    onSuccess: () => {
      onSuccess();
      message.success(t('ELEMENTS_MOVED_SUCCESS'));
    }
  });

  const { mutate: importSections, isPending: isImporting } = useMutation({
    mutationFn: (data: IImportSections) => CourseService.importSections(data),
    onSuccess: () => {
      onSuccess();
      message.success(t('SECTIONS_IMPORTED_SUCCESSFULLY'));
    }
  });

  const { mutate: unsyncSection, isPending: isUnsyncing } = useMutation({
    mutationFn: (sectionId: string) => SectionService.unsyncSection(sectionId),
    onSuccess: () => {
      onSuccess();
      message.success(t('SECTION_UNSYNCED_SUCCESSFULLY'));
    }
  });

  const { mutate: importActivities, isPending: isImportingActivities } = useMutation({
    mutationFn: (data: IImportActivities) => CourseService.importActivities(data),
    onSuccess: () => {
      onSuccess();
      message.success(t('ACTIVITIES_IMPORTED_SUCCESSFULLY'));
    }
  });

  return {
    createSection,
    updateSection,
    removeSection,
    unsyncSection,
    moveActivityBetweenSections,
    importSections,
    isImporting,
    isWorking:
      isCreating ||
      isUpdating ||
      isRemoving ||
      isMoving ||
      isImporting ||
      isUnsyncing ||
      isImportingActivities,
    importActivities,
    isImportingActivities
  };
};
